/* eslint-disable custom/class-methods-use-this */

import DOM from '../DOM/DOM'
import { URL } from '../URL/URL'

import Logger from '../Logger/Logger'

import type { TQuery, TRoutes, IClientRouter, TNotFoundCallback } from './definitions'
import Router from './Router'

const log = new Logger('ROUTER')

const { pathname } = window.location
const START_PATH = pathname.length ? pathname : '/'

export default class ClientRouter extends Router implements IClientRouter {
  protected readonly history: string[] = []

  /**
   * A Router inicializálása.
   * @param routes   - A megadott route-ok, amiket figyel a rendszer.
   * @param notFound - Ha nem talál route-ot az url alapján, ezt fogja meghívni.
   */
  public init (routes: TRoutes, notFound?: TNotFoundCallback): void {
    log.dev(`[${ this.namespace }][init]`, routes)

    this.listen()

    this.addRoutes(routes)

    this.notFoundCallback = notFound
  }

  /** Hallgatja a popstate-et, tehát az url változást. */
  public listen (): void {
    window.addEventListener('popstate', this.onPopState)
  }

  /**
   * Új URL létrehozás és popstate esemény kiváltása.
   * @param url   - Erre fogunk váltani.
   * @param query - Ezek a query paraméterek.
   */
  public push (url: string, query?: TQuery | string | null): void {
    this.history.push(url)

    const newUrl = URL.make(url, query)

    log.dev(`[${ this.namespace }][push]`, newUrl)

    this.pushState(newUrl)
  }

  /**
   * Hash beállítása a window.location.hash helyett.
   * @param hash - Az új hash.
   */
  public setHash (hash: string): void {
    this.pushState(`#${ hash.replace(/^#/, '') }`)
  }

  /**
   * Router futtatása.
   * @param url - Az őshöz képest ez kap default path-t.
   */
  public override run (url = START_PATH): Promise<void> {
    log.dev(`[${ this.namespace }][run]`, url)

    return super.run(url)
  }

  /** Visszaadja az utolsó előtti URL-t. */
  public getLastUrl (): string {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- Mi ez a "2" ?
    return this.history[this.history.length - 2] ?? '/'
  }

  /** Egy URL-t visszalépünk, olyan, mintha a böngészőben megnyomnánk a back gombot. */
  public back (): void {
    window.history.back()
  }

  /** Popstate eseménynél megfuttatja a run-t. Ez így megy: push, onPopState, run. */
  private readonly onPopState = (): void => {
    const { pathname: pname } = window.location
    this.run(pname)
  }

  /**
   * Wrapper a natív history pushState fölött.
   * @param newUrl - Az új URL.
   */
  private pushState (newUrl: string): void {
    // Élő hiba:
    // FF alatt lehet NS_ERROR_FAILURE
    try {
      window.history.pushState(null, '', newUrl)
    }
    catch {
      window.location.href = newUrl
    }

    DOM.dispatch(window, 'popstate')
  }
}
